import { UniqueIdentifier } from "@dnd-kit/core";
import {
  ResourceCategoryResponse,
  ResourceOrderPayload,
  ScheduleResourceResponse,
  compareByAdminOrder,
} from "app/utils/request/scheduling/facilityResources";

// One section of the Resources page with its resources.
export interface SectionGroup {
  category: ResourceCategoryResponse;
  resources: ScheduleResourceResponse[];
}

/**
 * The sections of the Resources page, each with its resources, in the order
 * an admin sets. `active` takes no part in the order, so a switch does not
 * move a card. A section with no resource stays in the list.
 */
export function groupSections(
  categories: ResourceCategoryResponse[],
  resources: ScheduleResourceResponse[],
): SectionGroup[] {
  return [...categories].sort(compareByAdminOrder).map((category) => ({
    category,
    resources: resources
      .filter((resource) => resource.resourceCategoryId === category.id)
      .sort(compareByAdminOrder),
  }));
}

/**
 * A copy of the list with one item moved from one index to another. The
 * reorder mode calls it for a section drop and for a card drop.
 */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);

  return next;
}

// The drag ids of the reorder mode. A section and a card live in different
// drag contexts, so the prefix only makes the ids easy to read.
export const sectionDragId = (categoryId: number) => `section-${categoryId}`;
export const cardDragId = (resourceId: number) => `resource-${resourceId}`;

// The part of a dnd-kit drag end event that the reorder mode reads.
export interface DropEvent {
  active: { id: UniqueIdentifier };
  over: { id: UniqueIdentifier } | null;
}

/**
 * A copy of the list with the dragged item put at the place of the item under
 * it. A drop outside the list, on the item itself or on an unknown id gives
 * the input list back.
 */
function dropById<T>(
  items: T[],
  dragId: (item: T) => string,
  { active, over }: DropEvent,
): T[] {
  if (over === null || active.id === over.id) {
    return items;
  }
  const from = items.findIndex((item) => dragId(item) === active.id);
  const to = items.findIndex((item) => dragId(item) === over.id);
  if (from === -1 || to === -1) {
    return items;
  }

  return moveItem(items, from, to);
}

/**
 * The sections after a section drop of the reorder mode.
 */
export function dropSection(
  groups: SectionGroup[],
  event: DropEvent,
): SectionGroup[] {
  return dropById(groups, (group) => sectionDragId(group.category.id), event);
}

/**
 * The sections after a card drop of the reorder mode. Only the section of the
 * card changes: a card never leaves its section (decision Q10).
 */
export function dropCard(
  groups: SectionGroup[],
  categoryId: number,
  event: DropEvent,
): SectionGroup[] {
  return groups.map((group) => {
    if (group.category.id !== categoryId) {
      return group;
    }
    const resources = dropById(
      group.resources,
      (resource) => cardDragId(resource.id),
      event,
    );

    return resources === group.resources ? group : { ...group, resources };
  });
}

/**
 * The body of the sort request: every section and every resource the page
 * shows, in page order. The API gives each id its place as its rank, so no
 * visible record keeps an empty rank after a save.
 */
export function orderPayload(groups: SectionGroup[]): ResourceOrderPayload {
  return {
    resourceCategoryIds: groups.map((group) => group.category.id),
    scheduleResourceIds: ([] as number[]).concat(
      ...groups.map((group) => group.resources.map((resource) => resource.id)),
    ),
  };
}
